import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { api } from '../api';
import { registerClips } from '../lib/phrases';
import { dedupeAlphabetical } from '../lib/words';
import { useBackgroundPrecache } from '../hooks/useBackgroundPrecache';
import type {
  Word,
  BuscarScene,
  Cuento,
  JwHistoria,
  JwComic,
  JwVideo,
  Oracion,
  MascotasData,
  LetterSounds,
  CategoryConfig,
  Config
} from '../types';

type DataState = {
  items: Word[];
  scenes: BuscarScene[];
  cuentos: Cuento[];
  jwHistorias: JwHistoria[];
  jwComics: JwComic[];
  jwVideos: JwVideo[];
  oraciones: Oracion[];
  mascotas: MascotasData;
  letterSounds: LetterSounds;
  categories: CategoryConfig[];
  config: Config;
  loading: boolean;
  error: Error | null;
  byTag: Record<string, Word[]>;
  allWords: Word[];
  refresh: () => void;
};

const DEFAULT_CONFIG: Config = {
  tts_lang: 'es-AR',
  tts_rate: 0.9,
  tts_pitch: 1.0
};

const NO_MASCOTAS: MascotasData = { mascotas: [], placements: {} };

const DataContext = createContext<DataState | null>(null);

function asNumber(v: unknown, fallback: number): number {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeConfig(raw: unknown): Config {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    tts_lang: (r.tts_lang as string) || DEFAULT_CONFIG.tts_lang,
    tts_rate: asNumber(r.tts_rate, DEFAULT_CONFIG.tts_rate),
    tts_pitch: asNumber(r.tts_pitch, DEFAULT_CONFIG.tts_pitch),
    // '' es "Automática" (ver AdminConfig): se guarda así porque un undefined
    // no viaja en el JSON y D1 se quedaba con la voz elegida antes.
    tts_voice: (r.tts_voice as string) || undefined,
    memotest_items: asNumber(r.memotest_items, 8),
    drag_items: asNumber(r.drag_items, 3)
  };
}

function groupByTag(items: Word[]): Record<string, Word[]> {
  const out: Record<string, Word[]> = {};
  for (const item of items) {
    const tags = Array.isArray(item.tags) && item.tags.length > 0
      ? item.tags
      : [item.type || 'OTROS'];
    for (const tag of tags) {
      if (!out[tag]) out[tag] = [];
      out[tag].push(item);
    }
  }
  return out;
}

export function DataProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Word[]>([]);
  const [scenes, setScenes] = useState<BuscarScene[]>([]);
  const [cuentos, setCuentos] = useState<Cuento[]>([]);
  const [jwHistorias, setJwHistorias] = useState<JwHistoria[]>([]);
  const [jwComics, setJwComics] = useState<JwComic[]>([]);
  const [jwVideos, setJwVideos] = useState<JwVideo[]>([]);
  const [oraciones, setOraciones] = useState<Oracion[]>([]);
  const [mascotas, setMascotas] = useState<MascotasData>(NO_MASCOTAS);
  const [letterSounds, setLetterSounds] = useState<LetterSounds>({});
  const [categories, setCategories] = useState<CategoryConfig[]>([]);
  const [config, setConfig] = useState<Config>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const tasks = [
      api.getData().then((d) => !cancelled && setItems(d)),
      api.getConfig().then((c) => {
        if (cancelled) return;
        setConfig(normalizeConfig(c.config));
        if (Array.isArray(c.categories)) setCategories(c.categories);
      }),
      api.getBuscar().then((s) => !cancelled && setScenes(s)).catch(() => {}),
      api.getCuentos().then((c) => !cancelled && setCuentos(c)).catch(() => {}),
      api.getJwHistorias().then((h) => !cancelled && setJwHistorias(h)).catch(() => {}),
      api.getJwComics().then((c) => !cancelled && setJwComics(c)).catch(() => {}),
      api.getJwVideos().then((v) => !cancelled && setJwVideos(v)).catch(() => {}),
      api.getOraciones().then((o) => !cancelled && setOraciones(o)).catch(() => {}),
      api.getMascotas().then((m) => !cancelled && setMascotas(m)).catch(() => {}),
      api.getLetterSounds().then((l) => !cancelled && setLetterSounds(l)).catch(() => {})
    ];

    Promise.allSettled(tasks).then((results) => {
      if (cancelled) return;
      const first = results.find((r) => r.status === 'rejected');
      if (first && first.status === 'rejected') {
        setError(first.reason instanceof Error ? first.reason : new Error(String(first.reason)));
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [nonce]);

  // Para el "¡Muy bien!" de celebrate(), que no es un componente.
  useEffect(() => registerClips(letterSounds), [letterSounds]);

  const byTag = useMemo(() => groupByTag(items), [items]);
  const allWords = useMemo(() => dedupeAlphabetical(items), [items]);

  useBackgroundPrecache(items, scenes, cuentos, jwHistorias, jwComics, jwVideos, letterSounds, loading);

  const value: DataState = {
    items,
    scenes,
    cuentos,
    jwHistorias,
    jwComics,
    jwVideos,
    oraciones,
    mascotas,
    letterSounds,
    categories,
    config,
    loading,
    error,
    byTag,
    allWords,
    refresh
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData(): DataState {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within <DataProvider>');
  return ctx;
}
